import React from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatDatePH, formatDateTimePH } from "../../utils/dateFormat";
import LeaveAttachmentLink from "./LeaveAttachmentLink";
import { API_BASE } from "../../utils/apiBase";

const formatStatusLabel = (value) => {
  const raw = String(value || "pending").replace(/_/g, " ").trim();
  if (!raw) return "Pending";
  return raw.replace(/\b\w/g, (match) => match.toUpperCase());
};

const getSubmittedAt = (row) => row?.date_submitted || row?.date_requested || row?.created_at || null;

const canUploadFollowupAttachment = (row) => {
  if (!row?.id) return false;
  if (row?.file_url || row?.file_path || row?.attachment) return false;
  const status = String(row?.status || "").toLowerCase();
  if (status.includes("reject") || status.includes("cancel")) return false;
  return true;
};

export default function LeaveRequestsTable({
  rows = [],
  loading = false,
  error = "",
  emptyText = "No leave requests found.",
  allowFollowupUpload = false,
  onAttachmentUploaded = null,
}) {
  const [uploadingId, setUploadingId] = React.useState(null);
  const [uploadMessage, setUploadMessage] = React.useState("");
  const [uploadError, setUploadError] = React.useState("");

  const uploadFollowupAttachment = async (row, file) => {
    if (!row?.id || !file) return;
    setUploadingId(row.id);
    setUploadMessage("");
    setUploadError("");
    try {
      const form = new FormData();
      form.append("attachment", file);
      await axios.post(`${API_BASE}/api/leave-requests/${encodeURIComponent(row.id)}/attachment`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadMessage("Follow-up attachment uploaded.");
      if (typeof onAttachmentUploaded === "function") {
        await onAttachmentUploaded();
      }
    } catch (err) {
      setUploadError(err?.response?.data?.error || err?.response?.data?.message || "Failed to upload follow-up attachment.");
    } finally {
      setUploadingId(null);
    }
  };

  const renderAttachmentCell = (row) => {
    const hasAttachment = Boolean(row?.file_url || row?.file_path || row?.attachment);
    if (hasAttachment) return <LeaveAttachmentLink row={row} />;
    if (!allowFollowupUpload || !canUploadFollowupAttachment(row)) return <LeaveAttachmentLink row={row} />;
    const inputId = `leave-followup-${row.id}`;
    const isUploading = uploadingId === row.id;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography component="span" variant="body2" color="text.secondary">-</Typography>
        <Button size="small" variant="outlined" component="label" disabled={isUploading}>
          {isUploading ? <CircularProgress size={16} /> : "Upload Proof"}
          <input
            id={inputId}
            hidden
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              event.target.value = "";
              if (file) uploadFollowupAttachment(row, file);
            }}
          />
        </Button>
      </Box>
    );
  };

  if (loading) {
    return <Typography>Loading leave requests...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!rows || rows.length === 0) {
    return <Typography>{emptyText}</Typography>;
  }

  return (
    <Box>
      {uploadMessage && <Alert severity="success" sx={{ mb: 1 }}>{uploadMessage}</Alert>}
      {uploadError && <Alert severity="error" sx={{ mb: 1 }}>{uploadError}</Alert>}
      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.5 }}>
        {rows.map((row, index) => {
          const submittedAt = getSubmittedAt(row);
          return (
            <Paper key={row?.id || index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {row?.leave_type || "-"}
              </Typography>
              <Typography variant="body2"><strong>Start:</strong> {row?.start_date ? formatDatePH(row.start_date) : "-"}</Typography>
              <Typography variant="body2"><strong>End:</strong> {row?.end_date ? formatDatePH(row.end_date) : "-"}</Typography>
              <Typography variant="body2"><strong>Requested:</strong> {submittedAt ? formatDateTimePH(submittedAt) : "-"}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-word" }}><strong>Reason:</strong> {row?.reason || "-"}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }} component="div"><strong>Attachment:</strong> {renderAttachmentCell(row)}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Status:</strong> {formatStatusLabel(row?.status)}</Typography>
            </Paper>
          );
        })}
      </Box>

      <TableContainer sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>Leave Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Date Requested</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Attachment</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const submittedAt = getSubmittedAt(row);
              return (
                <TableRow key={row?.id || index}>
                  <TableCell>{row?.leave_type || "-"}</TableCell>
                  <TableCell>{row?.start_date ? formatDatePH(row.start_date) : "-"}</TableCell>
                  <TableCell>{row?.end_date ? formatDatePH(row.end_date) : "-"}</TableCell>
                  <TableCell>{submittedAt ? formatDateTimePH(submittedAt) : "-"}</TableCell>
                  <TableCell>{row?.reason || "-"}</TableCell>
                  <TableCell>{renderAttachmentCell(row)}</TableCell>
                  <TableCell>{formatStatusLabel(row?.status)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
